import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, ApiError } from "../../lib/api.js";
import { LoadingSpinner } from "../../components/LoadingSpinner.js";
import { useT, LanguageSwitcher, type Translate } from "../../i18n/index.js";
import type { Quiz } from "./types.js";
import { QuizImportPanel } from "./QuizImportPanel.js";
import {
  accentRule,
  badge,
  errorText,
  ghostButton,
  pageShell,
  primaryButton,
  surface,
} from "./quiz-ui.js";

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; quizzes: Quiz[] };

export function QuizList() {
  const navigate = useNavigate();
  const t = useT();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [isCreating, setIsCreating] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const loadQuizzes = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const data = await api.get<{ quizzes: Quiz[] }>("/quizzes");
      setState({ kind: "ready", quizzes: data.quizzes });
    } catch (err) {
      setState({
        kind: "error",
        message:
          err instanceof ApiError ? err.message : t("quiz.loadFailed"),
      });
    }
  }, [t]);

  useEffect(() => {
    void loadQuizzes();
  }, [loadQuizzes]);

  const handleCreate = async () => {
    setCreateError(null);
    setIsCreating(true);
    try {
      const data = await api.post<{ quiz: Quiz }>("/quizzes", {
        title: t("quiz.untitled"),
      });
      navigate(`/admin/quizzes/${data.quiz.id}/edit`);
    } catch (err) {
      setCreateError(
        err instanceof ApiError ? err.message : t("quiz.createFailed"),
      );
      setIsCreating(false);
    }
  };

  return (
    <main style={pageShell}>
      <header
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "2rem",
        }}
      >
        <div>
          <hr style={accentRule} />
          <h1 style={{ margin: 0, fontSize: "clamp(1.8rem, 1.4rem + 2vw, 2.6rem)" }}>
            {t("quiz.yourQuizzes")}
          </h1>
          <p style={{ margin: "0.5rem 0 0", maxWidth: "34rem", opacity: 0.75 }}>
            {t("quiz.shelfIntro")}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
          <LanguageSwitcher compact />
          <button
            type="button"
            onClick={() => setIsImportOpen((open) => !open)}
            style={ghostButton}
          >
            {t("quiz.importJson")}
          </button>
          <button
            type="button"
            className="btn-tactile"
            onClick={handleCreate}
            disabled={isCreating}
            style={{ ...primaryButton, opacity: isCreating ? 0.7 : 1 }}
          >
            {isCreating ? t("quiz.creating") : t("quiz.newQuiz")}
          </button>
        </div>
      </header>

      {isImportOpen && (
        <div style={{ marginBottom: "1.5rem" }}>
          <QuizImportPanel
            onImported={(quiz) => navigate(`/admin/quizzes/${quiz.id}/edit`)}
            onCancel={() => setIsImportOpen(false)}
          />
        </div>
      )}

      {createError && (
        <p role="alert" style={{ ...errorText, marginBottom: "1rem" }}>
          {createError}
        </p>
      )}

      {state.kind === "loading" && <LoadingSpinner label={t("quiz.loadingQuizzes")} />}

      {state.kind === "error" && (
        <div
          role="alert"
          style={{ ...surface, padding: "1.5rem", display: "grid", gap: "0.85rem" }}
        >
          <p style={{ ...errorText }}>{state.message}</p>
          <button
            type="button"
            onClick={() => void loadQuizzes()}
            style={{ ...ghostButton, justifySelf: "start" }}
          >
            {t("common.tryAgain")}
          </button>
        </div>
      )}

      {state.kind === "ready" && state.quizzes.length === 0 && (
        <div
          style={{
            ...surface,
            padding: "3rem 2rem",
            textAlign: "center",
            display: "grid",
            gap: "0.75rem",
            placeItems: "center",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "1.35rem" }}>{t("quiz.noQuizzesTitle")}</h2>
          <p style={{ margin: 0, maxWidth: "26rem", opacity: 0.75 }}>
            {t("quiz.noQuizzesBody")}
          </p>
          <button
            type="button"
            className="btn-tactile"
            onClick={handleCreate}
            disabled={isCreating}
            style={{ ...primaryButton, marginTop: "0.5rem" }}
          >
            {t("quiz.createFirst")}
          </button>
        </div>
      )}

      {state.kind === "ready" && state.quizzes.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gap: "0.75rem",
          }}
        >
          {state.quizzes.map((quiz) => (
            <QuizRow key={quiz.id} quiz={quiz} t={t} />
          ))}
        </ul>
      )}
    </main>
  );
}

function QuizRow({ quiz, t }: { quiz: Quiz; t: Translate }) {
  const questionCount = quiz.questionCount ?? 0;
  return (
    <li>
      <Link
        to={`/admin/quizzes/${quiz.id}/edit`}
        style={{
          ...surface,
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          padding: "1.1rem 1.25rem",
          textDecoration: "none",
          color: "inherit",
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: "block",
              fontSize: "1.15rem",
              fontWeight: 700,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {quiz.title}
          </span>
          <span style={{ fontSize: "0.9rem", opacity: 0.7 }}>
            {questionCount === 1
              ? t("quiz.oneQuestion")
              : t("quiz.questionCount", { count: questionCount })}
          </span>
        </span>
        <span style={badge(quiz.isPublished)}>
          {quiz.isPublished ? t("quiz.published") : t("quiz.draft")}
        </span>
        <span aria-hidden="true" style={{ fontSize: "1.3rem", opacity: 0.5 }}>
          &rsaquo;
        </span>
      </Link>
    </li>
  );
}
